import { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, collection, deleteDoc, getFirestore, query, where, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useLoader } from '../contexts/LoaderContext';
import { logError } from '../utils/analytics';
import { SHOP_TYPES } from '../constants/shopTypes';
import { convertToWebP } from '../utils/imageUpload';
import moment from 'moment';
import { useAppStore } from "../store";
import { selectCurrentUserRecord } from "../store/common/userRecord/slice";


export const useArticle = () => {
    const { showLoader, hideLoader } = useLoader();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

  const userRecord = useAppStore(selectCurrentUserRecord);

    // Upload image to Firebase Storage with WebP conversion
    const uploadImage = async (file, path = 'articles') => {
        try {
            const storage = getStorage();
            // Use more precise timestamp with random component to avoid collisions
            const timestamp = moment(serverTimestamp()).unix();
            const randomId = Math.random().toString(36).substring(2, 8);
            const fileExt = (file && typeof file.name === 'string')
                ? (file.name.split('.').pop()?.toLowerCase?.() || '')
                : '';
            const fileName = `${path}/${userRecord?.id || userRecord?.uid}/${timestamp}_${randomId}.webp`; // Always use .webp extension
            const storageRef = ref(storage, fileName);

            // Convert to WebP if not already
            const fileToUpload = file.type === 'image/webp' ? file : await convertToWebP(file);

            // Set metadata for WebP
            const metadata = {
                contentType: 'image/webp',
                cacheControl: 'public, max-age=31536000', // 1 year cache
            };

            const uploadResult = await uploadBytes(storageRef, fileToUpload, metadata);
            const downloadURL = await getDownloadURL(storageRef);
            
            const result = {
                url: downloadURL,
                path: fileName,
                format: 'webp',
                size: fileToUpload.size,
                dimensions: {
                    width: fileToUpload.width,
                    height: fileToUpload.height
                },
                metadata: {
                    fullPath: uploadResult.metadata.fullPath,
                    bucket: uploadResult.metadata.bucket,
                    generation: uploadResult.metadata.generation,
                    metageneration: uploadResult.metadata.metageneration,
                    timeCreated: uploadResult.metadata.timeCreated,
                    updated: uploadResult.metadata.updated
                }
            };
            
            return result;
        } catch (error) {
            console.error('Error uploading image:', error);
            throw error;
        }
    };

    // Process article images before saving
    const processArticleImages = async (articleData) => {
        // Create a deep copy to avoid reference issues
        const processedData = JSON.parse(JSON.stringify(articleData));

        // Handle thumbnail
        if (processedData.thumbnail instanceof File) {
            const { url, path } = await uploadImage(processedData.thumbnail, 'article-thumbnails');
            processedData.thumbnail = {
                url,
                path,
                alt: `Thumbnail for ${processedData.title || 'article'}`,
                uploadedAt: moment().unix()
            };
        }

        // Handle main image
        if (articleData.image?.file) {
            // Handle both direct File object and object with objectURL property
            const fileToUpload = articleData.image.file instanceof File
                ? articleData.image.file
                : (articleData.image.file?.objectURL ? new File([articleData.image.file.objectURL], articleData.image.file.name || 'image', { type: 'image/*' }) : null);

            if (fileToUpload) {
                const { url, path } = await uploadImage(fileToUpload, 'articles');
                processedData.image = {
                    url,
                    path,
                    alt: articleData.image.alt || articleData.title
                };
            }
        }

        // Process section images if it's a gallery article
        if (articleData.sections) {
            for (let i = 0; i < articleData.sections.length; i++) {
                const section = articleData.sections[i];

                if (section.images) {
                    for (let j = 0; j < section.images.length; j++) {
                        const image = section.images[j];

                        if (image?.file) {
                            // Handle both direct File object and object with objectURL property
                            const fileToUpload = image.file instanceof File
                                ? image.file
                                : (image.file?.objectURL ? new File([image.file.objectURL], image.file.name || 'image', { type: 'image/*' }) : null);

                            if (fileToUpload) {
                                const { url, path } = await uploadImage(fileToUpload, 'articles/sections');
                                // Create a completely new object to avoid any reference issues
                                processedData.sections[i].images[j] = {
                                    id: image.id,
                                    url,
                                    path,
                                    alt: image.alt || `Section ${i + 1} - Image ${j + 1}`,
                                    uploadedAt: moment().unix()
                                };
                            }
                        }
                    }
                }
            }
        }

        return processedData;
    };

    // Clean up article data before saving to Firestore
    const cleanArticleData = (data) => {
        const cleaned = { ...data };

        // Remove any file objects
        if (cleaned.image?.file) {
            const { file, ...imageWithoutFile } = cleaned.image;
            cleaned.image = imageWithoutFile;
        }
        
        // Handle thumbnail if it's a File object (should be processed by now)
        if (cleaned.thumbnail instanceof File) {
            // If we somehow still have a File object, remove it
            delete cleaned.thumbnail;
        }

        // Clean section images
        if (cleaned.sections) {
            cleaned.sections = cleaned.sections.map(section => {
                if (!section.images) return section;
                return {
                    ...section,
                    images: section.images.map(image => {
                        if (image?.file) {
                            const { file, ...imageWithoutFile } = image;
                            return imageWithoutFile;
                        }
                        return image;
                    })
                };
            });
        }

        return cleaned;
    };

    // Save article to Firestore
    const saveArticle = async (articleData, templateType) => {
        if (!userRecord?.uid || !userRecord.id) {
            throw new Error('User not authenticated');
        }

        try {
            setLoading(true);
            const db = getFirestore();
            const articlesRef = collection(db, 'articles');

            // Process images first (this handles the file uploads)
            const processedData = await processArticleImages(articleData);

            // Clean the data before saving to Firestore
            const cleanedData = cleanArticleData(processedData);

            const articleId = articleData.id || doc(articlesRef).id;

            const articleToSave = {
                ...cleanedData,
                id: articleId,
                templateType, // Include the template type
                authorId: userRecord?.id || userRecord?.uid,
                status: articleData.status || 'DRAFT',
                createdAt: moment(articleData.createdAt).unix() || moment(serverTimestamp()).unix(),
                updatedAt: moment(serverTimestamp()).unix(),
            };

            await setDoc(doc(articlesRef, articleId), articleToSave, { merge: true });

            return { success: true, id: articleId };
        } catch (error) {
            console.error('Error saving article:', error);
            logError('useArticle: saveArticle', error);
            setError(error.message);
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    // Get articles for the current user with a fallback for missing index
    const getArticles = async () => {
        if (!userRecord?.id || !userRecord?.uid) return [];

        try {
            setLoading(true);
            const db = getFirestore();
            const articlesRef = collection(db, 'articles');

            try {
                // First try with the indexed query
                const q = query(
                    articlesRef,
                    where('authorId', '==', userRecord?.id || userRecord?.uid),
                    orderBy('updatedAt', 'desc')
                );

                const querySnapshot = await getDocs(q);
                const articlesList = [];

                querySnapshot.forEach((doc) => {
                    articlesList.push({ id: doc.id, ...doc.data() });
                });

                setArticles(articlesList);
                return articlesList;
            } catch (error) {
                if (error.code === 'failed-precondition') {
                    // Fallback to client-side sorting
                    // Fallback: Get all user's articles and sort client-side
                    const q = query(
                        articlesRef,
                        where('authorId', '==', userRecord?.id || userRecord?.uid)
                    );

                    const querySnapshot = await getDocs(q);
                    const articlesList = [];

                    querySnapshot.forEach((doc) => {
                        articlesList.push({ id: doc.id, ...doc.data() });
                    });

                    // Sort by updatedAt in descending order
                    const sortedArticles = articlesList.sort((a, b) =>
                        (b.updatedAt?.toDate?.() || 0) - (a.updatedAt?.toDate?.() || 0)
                    );

                    setArticles(sortedArticles);
                    return sortedArticles;
                }
                throw error; // Re-throw if it's a different error
            }
        } catch (error) {
            console.error('Error getting articles:', error);
            logError('useArticle: getArticles', error);
            setError(error.message);
            return [];
        } finally {
            setLoading(false);
        }
    };

    // Get a single article by ID
    const getArticleById = async (id) => {
        if (!userRecord?.id || !userRecord?.uid || !id) return null;

        try {
            setLoading(true);
            const db = getFirestore();
            const articleDoc = await getDoc(doc(db, 'articles', id));

            if (articleDoc.exists()) {
                return { id: articleDoc.id, ...articleDoc.data() };
            }
            return null;
        } catch (error) {
            console.error('Error getting article:', error);
            logError('useArticle: getArticleById', error);
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Delete an article
    const deleteArticle = async (id) => {
        if (!userRecord?.id || !userRecord?.uid || !id) {
            setError('Missing user ID or article ID');
            return false;
        }

        try {
            setLoading(true);
            const db = getFirestore();

            // First check if the article exists and belongs to the user
            const articleRef = doc(db, 'articles', id);
            const articleDoc = await getDoc(articleRef);

            if (!articleDoc.exists()) {
                setError('Article not found');
                return false;
            }

            const articleData = articleDoc.data();
            if (articleData.authorId !== userRecord.uid) {
                setError('Permission denied: Article belongs to another user');
                return false;
            }

            // Delete the article
            await deleteDoc(articleRef);

            // Update local state
            setArticles(prevArticles => prevArticles.filter(article => article.id !== id));
            return true;
        } catch (error) {
            console.error('Error deleting article:', error);
            logError('useArticle: deleteArticle', error);
            setError(error.message);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Load articles on mount
    useEffect(() => {
        if (userRecord?.id || userRecord?.uid) {
            getArticles().catch(() => { });
        } else {
            setArticles([]);
        }
    }, [userRecord?.uid, userRecord?.id]);


    // Convert shopType values to clean labels
    const cleanShopType = userRecord?.shopType ? (
        Array.isArray(userRecord.shopType)
          ? userRecord.shopType.map(type => {
              const item = SHOP_TYPES.flatMap(category => category.items)
                .find(item => item.value === type);
              return item ? item.label : type;
            })
          : Object.values(userRecord.shopType)
              .flat()
              .flatMap(category => Object.values(category))
              .map(type => {
                const item = SHOP_TYPES.flatMap(category => category.items)
                  .find(item => item.value === type);
                return item ? item.label : type;
              })
    ) : null;

    return {
        shopType: cleanShopType,
        articles,
        loading,
        error,
        saveArticle,
        getArticles,
        getArticleById,
        deleteArticle,
    };
};

export default useArticle;
